import { useQuery } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { listWorkers, quoteAssignment } from '../../api/resources';
import { inr } from '../../utils/format';

// Work types, and which worker roles normally do each — used to list the
// suitable workers first. Anyone can still be picked.
export const WORK_TYPES = [
  { value: 'CUTTING', label: 'Cutting', roles: ['CUTTING_MASTER', 'ALL_ROUNDER'] },
  { value: 'STITCHING', label: 'Stitching', roles: ['SEWING_TAILOR', 'ALL_ROUNDER'] },
];
const WORK_LABEL = Object.fromEntries(WORK_TYPES.map((t) => [t.value, t.label]));

const STATUS_OPTIONS = [
  { value: 'ASSIGNED', label: 'Assigned' },
  { value: 'IN_PROGRESS', label: 'In progress' },
  { value: 'COMPLETED', label: 'Completed' },
];

const hasValue = (v) => v !== '' && v != null;

function useActiveWorkers() {
  return useQuery({ queryKey: ['workers', 'ACTIVE'], queryFn: () => listWorkers({ status: 'ACTIVE' }) });
}

function Row({ type, entry, workers, tailoringItemId, qty, canOverride, onChange }) {
  const locked = !!entry?.settled;
  const workerId = entry?.workerId ? Number(entry.workerId) : null;
  const pieces = Number(qty) || 1;
  const itemId = tailoringItemId ? Number(tailoringItemId) : null;

  // Live rate-card cost. The server recomputes it on save regardless; this is
  // so the person assigning sees the figure before they commit.
  const quote = useQuery({
    queryKey: ['assign-quote', workerId, itemId, type.value, pieces],
    queryFn: () => quoteAssignment({ workerId, tailoringItemId: itemId, workType: type.value, qty: pieces }),
    enabled: !!workerId && !!itemId && !locked,
  });
  const calculated = locked
    ? entry.calculatedCost
    : (quote.data?.calculatedCost ?? (itemId ? entry?.calculatedCost ?? null : 0));
  const overridden = hasValue(entry?.overrideCost);
  const effective = overridden ? Number(entry.overrideCost) : calculated;

  // Somebody with no tailoring trade is left out: they do not make garments,
  // and the server refuses the job anyway.
  const makers = workers.filter((w) => w.role !== 'NO_TRADE');
  const suitable = makers.filter((w) => type.roles.includes(w.role));
  const others = makers.filter((w) => !type.roles.includes(w.role));
  // A saved assignment may point at a worker since deactivated — keep them
  // selectable so re-saving the order does not silently drop the job.
  const missing = workerId && !workers.some((w) => w.id === workerId)
    ? { id: workerId, name: entry.workerName || `Worker #${workerId}` }
    : null;

  const patch = (p) => onChange({ ...(entry || { workType: type.value, status: 'ASSIGNED' }), ...p });

  return (
    <div className={`grid items-center gap-2 rounded-lg border px-3 py-2 sm:grid-cols-[6rem_1fr_7rem_7rem_7.5rem] ${locked ? 'border-brand-100 bg-brand-50/40' : 'border-line'}`}>
      <span className="text-sm font-semibold text-content">{type.label}</span>

      <select
        className="input !py-1.5"
        value={workerId || ''}
        disabled={locked}
        aria-label={`${type.label} worker`}
        onChange={(e) => {
          const id = e.target.value;
          if (!id) return onChange(null);
          const w = workers.find((x) => x.id === Number(id));
          // Changing hands clears any override set for the previous worker.
          patch({ workerId: id, workerName: w?.name || '', calculatedCost: null, ...(Number(id) !== workerId ? { overrideCost: null, overrideReason: '' } : {}) });
        }}
      >
        <option value="">— Not assigned —</option>
        {missing && <option value={missing.id}>{missing.name} (inactive)</option>}
        {suitable.length > 0 && (
          <optgroup label={`${type.label} workers`}>
            {suitable.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </optgroup>
        )}
        {others.length > 0 && (
          <optgroup label="Other workers">
            {others.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </optgroup>
        )}
      </select>

      <span className="text-xs text-content-muted" title="From the rate card">
        {workerId ? <>Rate card <b className="tabular-nums text-content">{calculated != null ? inr(calculated) : '…'}</b></> : '—'}
      </span>

      <input
        className="input !py-1.5 text-right tabular-nums"
        type="number"
        min={0}
        step="0.01"
        placeholder={canOverride ? 'Override' : 'No override'}
        value={hasValue(entry?.overrideCost) ? entry.overrideCost : ''}
        disabled={locked || !workerId || !canOverride}
        title={canOverride ? 'Admin override of the worker cost' : 'Only an admin can override'}
        onChange={(e) => patch({ overrideCost: e.target.value === '' ? null : e.target.value })}
        aria-label={`${type.label} cost override`}
      />

      {locked ? (
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700">
          <Lock size={12} /> {entry.settlementNo || 'Settled'}
        </span>
      ) : (
        <select
          className="input !py-1.5"
          value={entry?.status || 'ASSIGNED'}
          disabled={!workerId}
          onChange={(e) => patch({ status: e.target.value })}
          aria-label={`${type.label} status`}
        >
          {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      )}

      {workerId && (
        <p className="text-xs text-content-subtle sm:col-span-5">
          Worker cost <b className={`tabular-nums ${overridden ? 'text-warning-700' : 'text-content'}`}>{effective != null ? inr(effective) : '…'}</b>
          {overridden && calculated != null && <> — overrides rate card {inr(calculated)}</>}
          {locked && ' · settled work is locked; release or reverse its settlement to change it'}
        </p>
      )}
    </div>
  );
}

/**
 * Worker assignments for one order line: at most one cutter and one stitcher.
 * `value` is the line's assignments array; `onChange` receives the new array.
 */
export default function AssignmentEditor({ value = [], onChange, tailoringItemId, qty, canOverride }) {
  const { data: workers = [], isLoading } = useActiveWorkers();
  const byType = Object.fromEntries(value.map((a) => [a.workType, a]));
  const setType = (workType, entry) => onChange([...value.filter((a) => a.workType !== workType), ...(entry ? [entry] : [])]);

  return (
    <div className="space-y-2">
      {!tailoringItemId && (
        <p className="rounded-lg bg-warning-50 px-3 py-2 text-xs text-warning-700">
          Choose a catalogue item to price the labour from the rate card. Without one, the worker cost is 0 unless an admin overrides it.
        </p>
      )}
      {isLoading && <p className="text-xs text-content-subtle">Loading workers…</p>}
      {WORK_TYPES.map((t) => (
        <Row
          key={t.value}
          type={t}
          entry={byType[t.value]}
          workers={workers}
          tailoringItemId={tailoringItemId}
          qty={qty}
          canOverride={canOverride}
          onChange={(entry) => setType(t.value, entry)}
        />
      ))}
    </div>
  );
}

/** Compact read-only summary for a line, e.g. "Cutting · John" chips. */
export function AssignmentChips({ assignments = [] }) {
  if (!assignments.length) return <span className="text-xs text-content-subtle">Unassigned</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {[...assignments].sort((a, b) => a.workType.localeCompare(b.workType)).map((a) => (
        <span
          key={a.workType}
          className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-2xs font-medium ${a.settled ? 'bg-brand-50 text-brand-700' : 'bg-surface-sunken text-content-muted'}`}
          title={a.settled ? `Settled in ${a.settlementNo || 'a settlement'}` : (a.status || '').replace('_', ' ').toLowerCase()}
        >
          {a.settled && <Lock size={10} />}
          {WORK_LABEL[a.workType]}: {a.workerName || `#${a.workerId}`}
        </span>
      ))}
    </span>
  );
}
